import { filterNewsEntries } from "../news-entries";
import { apiPath } from "../../lib/apiPath";
import { postWallAction } from "../../lib/cloud";
import { blockFactPool, pickFromFactPool, WALL_DEFAULT_FACTS } from "./default-facts";
import type { WallBlock, WallBlockType, WallLiveData } from "./types";

export type WallRenderMode = "view" | "preview" | "editor";

export type WallRenderContext = {
  mode: WallRenderMode;
  live: WallLiveData | null;
  authenticated: boolean;
  onNavigate?: (section: string) => void;
  onCounterUpdate?: (blockId: string, value: number) => void;
  onPollVoted?: () => void;
  onEditBlock?: (blockId: string) => void;
};

/** Active Wall countdown setIntervals — cleared on each re-render / screen close. */
const wallCountdownTimers: number[] = [];

export function clearWallCountdownTimers(): void {
  for (const id of wallCountdownTimers) window.clearInterval(id);
  wallCountdownTimers.length = 0;
}

function fmtNum(n: number): string {
  return n.toLocaleString(undefined);
}

function fmtPlaytime(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtSpeedrunMs(ms: number): string {
  const s = ms / 1000;
  if (s >= 60) {
    const m = Math.floor(s / 60);
    return `${m}m ${(s % 60).toFixed(2)}s`;
  }
  return `${s.toFixed(2)}s`;
}

function str(data: Record<string, unknown>, key: string, fallback = ""): string {
  const v = data[key];
  return typeof v === "string" ? v : fallback;
}

function bool(data: Record<string, unknown>, key: string, fallback = false): boolean {
  const v = data[key];
  return typeof v === "boolean" ? v : fallback;
}

function num(data: Record<string, unknown>, key: string, fallback = 0): number {
  const v = Number(data[key]);
  return Number.isFinite(v) ? v : fallback;
}

function wrapBlock(block: WallBlock, inner: HTMLElement, ctx: WallRenderContext): HTMLElement {
  const el = document.createElement("article");
  el.className = `wallBlock wallBlock--${block.type}`;
  el.dataset.blockId = block.id;
  el.dataset.blockType = block.type;
  if (ctx.mode === "editor") {
    el.draggable = true;
    const handle = document.createElement("div");
    handle.className = "wallBlockEditorHandle";
    handle.textContent = "⋮⋮";
    handle.title = "Drag to reorder";
    el.appendChild(handle);
  }
  el.appendChild(inner);
  return el;
}

function renderText(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--text";
  const title = str(block.data, "title");
  if (title) {
    const h = document.createElement("h3");
    h.className = "wallBlockTitle";
    h.textContent = title;
    body.appendChild(h);
  }
  const p = document.createElement("div");
  p.className = "wallBlockBody";
  p.textContent = str(block.data, "body");
  body.appendChild(p);
  return wrapBlock(block, body, ctx);
}

function renderHeader(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--header";
  const h = document.createElement("h2");
  h.className = "wallBlockHeaderTitle";
  h.textContent = str(block.data, "title", "Header");
  body.appendChild(h);
  const sub = str(block.data, "subtitle");
  if (sub) {
    const s = document.createElement("p");
    s.className = "wallBlockHeaderSub";
    s.textContent = sub;
    body.appendChild(s);
  }
  return wrapBlock(block, body, ctx);
}

function renderDivider(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const hr = document.createElement("hr");
  hr.className = "wallBlockDivider";
  return wrapBlock(block, hr, ctx);
}

function renderImage(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--image";
  const imageId = str(block.data, "imageId");
  const width = Math.min(100, Math.max(20, num(block.data, "width", 100)));
  if (imageId) {
    const img = document.createElement("img");
    img.className = "wallBlockImage";
    img.alt = str(block.data, "caption") || "Wall image";
    img.src = apiPath(`/api/wall-image?id=${encodeURIComponent(imageId)}`);
    img.style.width = `${width}%`;
    body.appendChild(img);
  } else {
    const ph = document.createElement("div");
    ph.className = "wallBlockImagePlaceholder";
    ph.textContent = ctx.mode === "editor" ? "Upload an image in the editor" : "";
    body.appendChild(ph);
  }
  const cap = str(block.data, "caption");
  if (cap) {
    const c = document.createElement("p");
    c.className = "wallBlockCaption";
    c.textContent = cap;
    body.appendChild(c);
  }
  return wrapBlock(block, body, ctx);
}

function renderQuote(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const q = document.createElement("blockquote");
  q.className = "wallBlockQuote";
  q.textContent = str(block.data, "text", "…");
  return wrapBlock(block, q, ctx);
}

function renderNews(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--news";
  const title = str(block.data, "title", "News");
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = title;
  body.appendChild(h);
  const ul = document.createElement("ul");
  ul.className = "wallBlockNewsList";
  const limit = Math.min(50, Math.max(1, num(block.data, "limit", 8)));
  for (const entry of filterNewsEntries("newest").slice(0, limit)) {
    const li = document.createElement("li");
    li.className = "wallBlockNewsItem";
    const t = document.createElement("strong");
    t.textContent = entry.title;
    const d = document.createElement("span");
    d.className = "wallBlockNewsDate";
    d.textContent = entry.date;
    li.append(t, document.createElement("br"), document.createTextNode(entry.description.slice(0, 160)), document.createElement("br"), d);
    ul.appendChild(li);
  }
  body.appendChild(ul);
  return wrapBlock(block, body, ctx);
}

function renderButton(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--button";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn green wallBlockBtn";
  btn.textContent = str(block.data, "label", "Button");
  btn.onclick = () => {
    const confirmNeeded = bool(block.data, "confirm");
    const confirmText = str(block.data, "confirmText", "Are you sure?");
    const run = () => {
      const action = str(block.data, "action");
      if (action === "link" && ctx.onNavigate) {
        ctx.onNavigate(str(block.data, "linkSection", "home"));
      }
    };
    if (confirmNeeded && !window.confirm(confirmText)) return;
    run();
  };
  body.appendChild(btn);
  return wrapBlock(block, body, ctx);
}

function renderGlobalCounter(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--globalCounter";
  const label = str(block.data, "label");
  if (label) {
    const h = document.createElement("h3");
    h.className = "wallBlockTitle";
    h.textContent = label;
    body.appendChild(h);
  }
  const countEl = document.createElement("div");
  countEl.className = "wallBlockCounterValue";
  const liveVal = ctx.live?.counters[block.id] ?? 0;
  countEl.textContent = bool(block.data, "showCount", true) ? fmtNum(liveVal) : "";
  body.appendChild(countEl);
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn wallBlockCounterBtn";
  btn.textContent = str(block.data, "buttonText", "Press");
  if (ctx.mode === "preview" || ctx.mode === "editor") {
    btn.disabled = true;
    btn.title = "Preview only";
  } else {
    btn.onclick = async () => {
      btn.disabled = true;
      try {
        const r = await postWallAction({
          action: "counter",
          blockId: block.id,
          cooldownSec: num(block.data, "cooldownSec"),
        });
        countEl.textContent = bool(block.data, "showCount", true) ? fmtNum(r.value ?? liveVal) : "";
        ctx.onCounterUpdate?.(block.id, r.value);
      } catch {
        btn.classList.add("wallBlockBtn--error");
      } finally {
        btn.disabled = false;
      }
    };
  }
  body.appendChild(btn);
  return wrapBlock(block, body, ctx);
}

function renderPoll(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--poll";
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = str(block.data, "question", "Poll");
  body.appendChild(h);
  const live = ctx.live?.polls[block.id];
  const options = live?.options ?? (Array.isArray(block.data.options) ? (block.data.options as string[]) : []);
  const list = document.createElement("div");
  list.className = "wallBlockPollOptions";
  options.forEach((opt, i) => {
    const row = document.createElement("div");
    row.className = "wallBlockPollRow";
    const pct = live?.percents[i] ?? 0;
    const bar = document.createElement("div");
    bar.className = "wallBlockPollBar";
    bar.style.width = `${pct}%`;
    const label = document.createElement("span");
    label.textContent = `${opt} — ${pct}%`;
    row.append(bar, label);
    if (ctx.mode === "view" && live?.myVote == null && ctx.authenticated) {
      const voteBtn = document.createElement("button");
      voteBtn.type = "button";
      voteBtn.className = "btn wallBlockPollVote";
      voteBtn.textContent = "Vote";
      voteBtn.onclick = async () => {
        try {
          await postWallAction({ action: "poll", blockId: block.id, optionIndex: i });
          ctx.onPollVoted?.();
        } catch {
          voteBtn.textContent = "Failed";
        }
      };
      row.appendChild(voteBtn);
    }
    list.appendChild(row);
  });
  if (!ctx.authenticated && ctx.mode === "view") {
    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent = "Sign in to vote.";
    body.appendChild(hint);
  }
  body.appendChild(list);
  return wrapBlock(block, body, ctx);
}

function renderProgress(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--progress";
  const title = str(block.data, "title");
  if (title) {
    const h = document.createElement("h3");
    h.className = "wallBlockTitle";
    h.textContent = title;
    body.appendChild(h);
  }
  const live = ctx.live?.progress[block.id] ?? { current: 0, goal: num(block.data, "goal", 1) };
  const track = document.createElement("div");
  track.className = "wallBlockProgressTrack";
  const fill = document.createElement("div");
  fill.className = "wallBlockProgressFill";
  const pct = live.goal > 0 ? Math.min(100, (live.current / live.goal) * 100) : 0;
  fill.style.width = `${pct}%`;
  track.appendChild(fill);
  const label = document.createElement("div");
  label.className = "wallBlockProgressLabel";
  label.textContent = `${fmtNum(live.current)} / ${fmtNum(live.goal)}`;
  body.append(track, label);
  return wrapBlock(block, body, ctx);
}

function renderStat(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--stat";
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = str(block.data, "title", "Statistics");
  body.appendChild(h);
  const grid = document.createElement("div");
  grid.className = "wallBlockStatGrid";
  const stats = ctx.live?.stats;
  const rows: [string, string][] = [];
  if (bool(block.data, "showGames")) rows.push(["Total games", fmtNum(stats?.totalGames ?? 0)]);
  if (bool(block.data, "showDeaths")) rows.push(["Total deaths", fmtNum(stats?.totalDeaths ?? 0)]);
  if (bool(block.data, "showPoints")) rows.push(["Total points", fmtNum(stats?.totalPoints ?? 0)]);
  if (bool(block.data, "showUsers")) rows.push(["Registered users", fmtNum(stats?.totalUsers ?? 0)]);
  if (bool(block.data, "showPlaytime")) rows.push(["Total playtime", fmtPlaytime(stats?.totalPlaytimeSec ?? 0)]);
  for (const [k, v] of rows) {
    const cell = document.createElement("div");
    cell.className = "wallBlockStatCell";
    cell.innerHTML = `<span>${k}</span><strong>${v}</strong>`;
    grid.appendChild(cell);
  }
  body.appendChild(grid);
  return wrapBlock(block, body, ctx);
}

function renderSpotlight(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--spotlight";
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = str(block.data, "title", "Player Spotlight");
  body.appendChild(h);
  const sp = ctx.live?.spotlight;
  if (!sp) {
    body.appendChild(document.createTextNode("No player data yet."));
    return wrapBlock(block, body, ctx);
  }
  const name = document.createElement("div");
  name.className = "wallBlockSpotlightName";
  name.textContent = sp.displayName;
  body.appendChild(name);
  if (sp.bio) {
    const bio = document.createElement("p");
    bio.className = "wallBlockSpotlightBio";
    bio.textContent = sp.bio;
    body.appendChild(bio);
  }
  const stats = document.createElement("p");
  stats.className = "accessHint";
  stats.textContent = `${fmtNum(sp.totalDeaths)} deaths · ${fmtPlaytime(sp.totalPlayTimeSec)} playtime`;
  body.appendChild(stats);
  return wrapBlock(block, body, ctx);
}

function renderFeaturedPreset(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--featuredPreset";
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = str(block.data, "title", "Featured Presets");
  body.appendChild(h);
  const ul = document.createElement("ul");
  ul.className = "wallBlockFeaturedList";
  const limit = num(block.data, "limit", 6);
  for (const p of (ctx.live?.featuredPresets ?? []).slice(0, limit)) {
    const li = document.createElement("li");
    li.textContent = `${p.name} — by ${p.displayName ?? "Player"}`;
    ul.appendChild(li);
  }
  if (!ul.childElementCount) {
    ul.appendChild(document.createElement("li")).textContent = "No public presets yet.";
  }
  body.appendChild(ul);
  return wrapBlock(block, body, ctx);
}

function renderFeaturedSpeedrun(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--featuredSpeedrun";
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = str(block.data, "title", "SpeedRun Records");
  body.appendChild(h);
  const ul = document.createElement("ul");
  ul.className = "wallBlockFeaturedList";
  const limit = num(block.data, "limit", 8);
  for (const r of (ctx.live?.featuredSpeedruns ?? []).slice(0, limit)) {
    const li = document.createElement("li");
    li.textContent = `${r.displayName ?? "Player"} — Level ${r.level} in ${fmtSpeedrunMs(r.timeMs)}`;
    ul.appendChild(li);
  }
  if (!ul.childElementCount) {
    ul.appendChild(document.createElement("li")).textContent = "No records yet.";
  }
  body.appendChild(ul);
  return wrapBlock(block, body, ctx);
}

function renderRandomFact(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--randomFact";
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = str(block.data, "title", "Did You Know?");
  body.appendChild(h);
  const p = document.createElement("p");
  p.className = "wallBlockFact";
  const fromLive = ctx.live?.randomFacts?.[block.id];
  if (fromLive) {
    p.textContent = fromLive;
  } else {
    const custom = blockFactPool(block.data);
    const pool = custom.length ? custom : WALL_DEFAULT_FACTS;
    p.textContent = pickFromFactPool(pool) || "No facts yet.";
  }
  body.appendChild(p);
  return wrapBlock(block, body, ctx);
}

function renderCountdown(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--countdown";
  const h = document.createElement("h3");
  h.className = "wallBlockTitle";
  h.textContent = str(block.data, "title", "Countdown");
  body.appendChild(h);
  const display = document.createElement("div");
  display.className = "wallBlockCountdown";
  const targetIso = str(block.data, "targetIso");
  const label = str(block.data, "label");
  const tick = () => {
    if (!targetIso) {
      display.textContent = "Set a target date in the editor.";
      return;
    }
    const target = new Date(targetIso).getTime();
    const diff = target - Date.now();
    if (diff <= 0) {
      display.textContent = label || "Event started!";
      return;
    }
    const d = Math.floor(diff / 86400000);
    const hrs = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    display.textContent = `${d}d ${hrs}h ${mins}m ${secs}s`;
  };
  tick();
  body.appendChild(display);
  const wrap = wrapBlock(block, body, ctx);
  if (ctx.mode === "view") {
    const timerId = window.setInterval(() => {
      if (!wrap.isConnected) {
        window.clearInterval(timerId);
        return;
      }
      tick();
    }, 1000);
    wallCountdownTimers.push(timerId);
  }
  return wrap;
}

function renderLink(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const body = document.createElement("div");
  body.className = "wallBlockInner wallBlockInner--link";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn wallBlockLinkBtn";
  btn.textContent = str(block.data, "label", "Go");
  btn.onclick = () => ctx.onNavigate?.(str(block.data, "section", "home"));
  body.appendChild(btn);
  return wrapBlock(block, body, ctx);
}

const RENDERERS: Record<WallBlockType, (block: WallBlock, ctx: WallRenderContext) => HTMLElement> = {
  text: renderText,
  header: renderHeader,
  divider: renderDivider,
  image: renderImage,
  quote: renderQuote,
  news: renderNews,
  button: renderButton,
  globalCounter: renderGlobalCounter,
  poll: renderPoll,
  progress: renderProgress,
  stat: renderStat,
  playerSpotlight: renderSpotlight,
  featuredPreset: renderFeaturedPreset,
  featuredSpeedrun: renderFeaturedSpeedrun,
  randomFact: renderRandomFact,
  countdown: renderCountdown,
  link: renderLink,
};

function renderBlockInner(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const fn = RENDERERS[block.type as WallBlockType];
  if (!fn) {
    const el = document.createElement("div");
    el.className = "wallBlockInner wallBlockInner--unknown";
    el.textContent = `Unknown block: ${block.type}`;
    return el;
  }
  return fn(block, ctx);
}

function renderStack(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  const card = document.createElement("article");
  card.className = "wallBlock wallBlock--stack";
  card.dataset.blockId = block.id;
  card.dataset.blockType = "stack";

  const inner = document.createElement("div");
  inner.className = "wallBlockStack";

  const items = Array.isArray(block.data.items) ? (block.data.items as WallBlock[]) : [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    if (item.type === "stack") continue;
    const slot = document.createElement("div");
    slot.className = "wallBlockStackItem";
    if (i > 0) {
      const sep = document.createElement("div");
      sep.className = "wallBlockStackSep";
      inner.appendChild(sep);
    }
    slot.appendChild(renderBlockInner(item, ctx));
    inner.appendChild(slot);
  }

  card.appendChild(inner);
  return card;
}

/** Add a case in RENDERERS when introducing a new block type. */
export function renderWallBlock(block: WallBlock, ctx: WallRenderContext): HTMLElement {
  if (block.type === "stack") return renderStack(block, ctx);
  return wrapBlock(block, renderBlockInner(block, ctx), ctx);
}

export function renderWallBlocks(
  blocks: WallBlock[],
  ctx: WallRenderContext,
  container: HTMLElement,
): void {
  clearWallCountdownTimers();
  container.replaceChildren();
  for (const block of blocks) {
    container.appendChild(renderWallBlock(block, ctx));
  }
}
